import {
  ExpenseReportByView,
  ReportByViewExpense,
} from "@common/utils/graphql";
import { useState } from "react";
import { ReportsCard } from "../components/ReportsCard";
import { NavigationTabs } from "@/components/ui/navigation-tabs";
import { PagePagination } from "@/components/ui/page-pagination";
import { PageWithTitle } from "@common/components/PageLayout/PageWithTitle";
import {
  AdminReportsSelectedTab,
  useAdminReportsTabs,
} from "./useAdminReportsTabs";
import { useAdminReportsData } from "./useAdminReportsData";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { ViewOrEditReportSheet } from "../sheets/ViewOrEditReportSheet";
import { ClockClockwise, FileDashed } from "@phosphor-icons/react";
import { NoResultsCard } from "@common/components";
import { ExpensePolicy } from "./components";

interface Props {
  tab: AdminReportsSelectedTab;
}

export interface ReportsTrigger {
  report: ExpenseReportByView;
  isEditModeDefault: boolean;
  expense?: ReportByViewExpense;
}

const noResultsContent = {
  [AdminReportsSelectedTab.History]: {
    Icon: FileDashed,
    header: "No approved or denied reports",
    body: "No reports have been approved or denied yet",
  },
  [AdminReportsSelectedTab.Pending]: {
    Icon: ClockClockwise,
    header: "No pending reports",
    body: "You have no pending reports.",
  },
};
export const AdminReports = ({ tab }: Props) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { tabs, status } = useAdminReportsTabs(tab, setCurrentPage);
  const { loadingReports, reports, hasMorePages } = useAdminReportsData(
    currentPage,
    status,
  );

  const [selectedReport, setSelectedReport] = useState<
    ReportsTrigger | undefined
  >(undefined);

  const resetSelectedReport = () => setSelectedReport(undefined);

  const results = reports.map((report) => (
    <ReportsCard
      key={report.id}
      mode="review"
      onClick={setSelectedReport}
      report={report}
    />
  ));

  if (tab === AdminReportsSelectedTab.PolicyManager) {
    return (
      <PageWithTitle title="Policy Manager">
        <NavigationTabs tabs={tabs} selectedTab={tab} />
        <ExpensePolicy />
      </PageWithTitle>
    );
  }

  const noResults = <NoResultsCard {...noResultsContent[tab]} />;

  return (
    <PageWithTitle title="Reports">
      <NavigationTabs tabs={tabs} selectedTab={tab} />
      {loadingReports && reports.length === 0 ? (
        <FullPageLoader />
      ) : reports.length > 0 ? (
        results
      ) : (
        noResults
      )}
      {!loadingReports && reports.length > 0 && (
        <PagePagination
          currentPage={currentPage}
          hasMorePages={hasMorePages}
          onChange={setCurrentPage}
        />
      )}
      <ViewOrEditReportSheet
        isOpen={!!selectedReport}
        selectedReport={
          {
            ...selectedReport!,
            isEditModeDefault: false,
          }!
        }
        onClose={resetSelectedReport}
      />
    </PageWithTitle>
  );
};
